/**
 * UserHomePage - 사용자 홈 대시보드
 *
 * 일반 사용자용 대시보드 페이지입니다.
 * - 통계: 사용 가능한 챗봇 · 실행 중인 스케줄 · 스케줄 실행 결과(최근 7일)
 * - 사용 가능한 챗봇: 질문 입력창 + 챗봇 목록 (고른 챗봇으로 바로 대화 시작)
 * - 실행 중인 스케줄: 가장 가까운 실행 + 스케줄 목록 (주기 · 지난 실행 결과 · 다음 실행)
 * 관리자 대시보드와 같은 공통 카드(.item-card) · 패널(.dash-card) 사용
 */

import { isAdminRole } from '../utils/role';
import { useState, useEffect, useLayoutEffect, useRef, CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, useToast } from '../components/ui';
import { authApi, workflowApi, Workflow, WorkflowRunLog } from '../api';
import BotIcon from '../assets/icons/dashboard/bot.svg?react';
import ChatIcon from '../assets/icons/dashboard/chat.svg?react';
import ClockIcon from '../assets/icons/dashboard/clock.svg?react';
import WorkflowIcon from '../assets/icons/dashboard/workflow.svg?react';
import ScheduleIcon from '../assets/icons/dashboard/calendar.svg?react';
import RefreshIcon from '../assets/icons/common/refresh.svg?react';
import SendIcon from '../assets/icons/common/send.svg?react';
import '../styles/adminPage.css';
import '../styles/userHome.css';

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const DAY_MAP: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
};
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const pad = (n: unknown) => String(n ?? 0).padStart(2, '0');

const getScheduleData = (workflow: Workflow) => {
  const node = workflow.nodes.find((n) => n.type === 'schedule-trigger');
  return node ? (node.data as Record<string, unknown>) : null;
};

// weeklyDay 는 숫자(1) 또는 문자열('monday') 둘 다 올 수 있음
const getWeeklyDay = (raw: unknown) =>
  typeof raw === 'number' ? raw : DAY_MAP[String(raw ?? '').toLowerCase()] ?? (Number(raw) || 1);

// 스케줄 실행 주기 포맷
const formatScheduleTime = (workflow: Workflow): string => {
  const d = getScheduleData(workflow);
  if (!d) return '';
  const type = (d.scheduleType as string) || 'daily';
  if (type === 'interval') return `${d.interval || 5}분 간격`;
  if (type === 'hourly') return `매시 ${pad(d.hourlyMinute)}분`;
  if (type === 'weekly') return `매주 ${DAYS[getWeeklyDay(d.weeklyDay)]}요일 ${pad(d.weeklyHour ?? 9)}:${pad(d.weeklyMinute)}`;
  if (type === 'cron') return d.cronExpression ? `Cron ${d.cronExpression}` : 'Cron';
  return `매일 ${pad(d.dailyHour ?? 9)}:${pad(d.dailyMinute)}`;
};

// 다음 실행 시각 계산 (백엔드 scheduler.service 의 getNextRunMs 와 같은 규칙)
const getNextRun = (workflow: Workflow, lastRun: WorkflowRunLog | undefined, now: Date): Date | null => {
  const d = getScheduleData(workflow);
  if (!d) return null;
  const type = (d.scheduleType as string) || 'daily';
  const next = new Date(now);

  if (type === 'cron') return null;
  if (type === 'interval') {
    const ms = Math.max(Number(d.interval) || 5, 1) * 60 * 1000;
    const base = lastRun ? new Date(lastRun.startedAt).getTime() : now.getTime();
    let t = base + ms;
    while (t <= now.getTime()) t += ms;
    return new Date(t);
  }
  if (type === 'hourly') {
    next.setMinutes(Number(d.hourlyMinute ?? 0), 0, 0);
    if (next <= now) next.setHours(next.getHours() + 1);
    return next;
  }
  if (type === 'weekly') {
    next.setHours(Number(d.weeklyHour ?? 9), Number(d.weeklyMinute ?? 0), 0, 0);
    let daysUntil = getWeeklyDay(d.weeklyDay) - now.getDay();
    if (daysUntil < 0 || (daysUntil === 0 && next <= now)) daysUntil += 7;
    next.setDate(next.getDate() + daysUntil);
    return next;
  }
  next.setHours(Number(d.dailyHour ?? 9), Number(d.dailyMinute ?? 0), 0, 0);
  if (next <= now) next.setDate(next.getDate() + 1);
  return next;
};

// 10.8(목) 02:00
const formatShort = (d: Date) =>
  `${d.getMonth() + 1}.${d.getDate()}(${DAYS[d.getDay()]}) ${pad(d.getHours())}:${pad(d.getMinutes())}`;

// 10월 8일 (목) 02:00
const formatLong = (d: Date) =>
  `${d.getMonth() + 1}월 ${d.getDate()}일 (${DAYS[d.getDay()]}) ${pad(d.getHours())}:${pad(d.getMinutes())}`;

// 오늘 / 내일 / N일 후
// 날짜 기준 며칠 뒤인지 (오늘 = 0)
const daysFrom = (d: Date, now: Date) => {
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((startOf(d) - startOf(now)) / (24 * 60 * 60 * 1000));
};

const formatRelativeDay = (d: Date, now: Date) => {
  const diff = daysFrom(d, now);
  if (diff <= 0) return '오늘';
  if (diff === 1) return '내일';
  return `${diff}일 후`;
};

const formatDate = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getMonth() + 1}.${d.getDate()}(${DAYS[d.getDay()]})`;
};

const formatUpdatedAt = (d: Date) =>
  `${d.toLocaleDateString('ko-KR')} ${pad(d.getHours())}:${pad(d.getMinutes())} 기준`;

// 챗봇 여부 판단 (스케줄/메일/웹훅 트리거가 없는 것)
const SCHEDULE_TRIGGER_TYPES = [
  'schedule-trigger',
  'mail-trigger',
  'webhook-trigger',
  'google-drive',
  'notion',
  'gmail',
  'naver',
];

const isChatbot = (w: Workflow) =>
  !w.nodes.some((n) => SCHEDULE_TRIGGER_TYPES.includes(n.type));

interface ScheduleRow {
  workflow: Workflow;
  cycle: string;
  lastRun?: WorkflowRunLog;
  nextRun: Date | null;
}

interface StatSegment {
  label: string;
  value: number;
  color: string;
}

interface StatCardProps {
  icon: ReactNode;
  label: ReactNode;
  value: number;
  unit: string;
  segments: StatSegment[];
  note: string;
  onClick: () => void;
}

// 통계 카드 - 숫자 · 비율 막대 · 범례 · 한 줄 (값이 0이어도 빈 막대로 틀 유지)
function StatCard({ icon, label, value, unit, segments, note, onClick }: StatCardProps) {
  return (
    <button type="button" className="item-card dash-stat" onClick={onClick}>
      <span className="item-card-icon item-card-icon--primary">{icon}</span>
      <div className="item-card-body">
        <div className="dash-stat-label">{label}</div>
        <div className="dash-stat-value">{value}<small>{unit}</small></div>
        <div className="dash-bar">
          {segments.filter((s) => s.value > 0).map((s) => (
            <i key={s.label} style={{ flex: s.value, background: s.color }} />
          ))}
        </div>
        <div className="dash-legend">
          {segments.map((s) => (
            <span key={s.label} style={{ '--c': s.color } as CSSProperties}>
              {s.label} <b>{s.value}</b>
            </span>
          ))}
        </div>
        <p className="dash-stat-note user-home-ellipsis">{note}</p>
      </div>
    </button>
  );
}

export function UserHomePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const user = authApi.getUser();
  const questionRef = useRef<HTMLTextAreaElement>(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [chatbots, setChatbots] = useState<Workflow[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [weekRuns, setWeekRuns] = useState({ success: 0, failed: 0 });
  const [myWorkflows, setMyWorkflows] = useState<{ deployed: number; draft: number; latest?: Workflow }>({
    deployed: 0,
    draft: 0,
  });
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [question, setQuestion] = useState('');

  // 질문 입력창 자동 높이 - 기본 2줄, 내용에 따라 최대 6줄까지 늘어나고 그 이상은 스크롤 (min/max-height 는 CSS)
  useLayoutEffect(() => {
    const el = questionRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [question, loading, chatbots.length]);

  useEffect(() => {
    loadData().finally(() => setLoading(false));
  }, []);

  const loadData = async () => {
    try {
      // 배포된 전체(챗봇 · 스케줄용) + 내 워크플로우(초안 포함, 관리자는 전체가 오므로 본인 것만 거름)
      const [data, allMine] = await Promise.all([
        workflowApi.list({ deployed: true }),
        workflowApi.list().catch(() => [] as Workflow[]),
      ]);
      const currentUser = authApi.getUser();
      const mine = allMine.filter((w) => w.owner === currentUser?.email && w.status !== 'archived');

      // 사용자는 공유된 챗봇 + 본인 것, 관리자는 전체 (최근 배포순)
      const bots = data
        .filter(isChatbot)
        .filter((w) => isAdminRole(currentUser?.role) || w.isSharedAsChatbot || w.owner === currentUser?.email)
        .sort(
          (a, b) =>
            new Date(b.deployedAt || b.createdAt).getTime() -
            new Date(a.deployedAt || a.createdAt).getTime()
        );

      let activeIds = new Set<string>();
      try {
        activeIds = new Set(await workflowApi.getActiveSchedules());
      } catch {
        /* 무시 */
      }

      const mySchedules = allMine.filter(
        (w) =>
          w.status === 'deployed' &&
          w.nodes.some((n) => n.type === 'schedule-trigger') &&
          activeIds.has(w.id) &&
          w.owner === currentUser?.email
      );

      // 스케줄별 실행 로그 (지난 실행 결과 · 최근 7일 집계)
      const logs = await Promise.all(
        mySchedules.map((w) => workflowApi.getLogs(w.id, 50).catch(() => [] as WorkflowRunLog[]))
      );

      const now = new Date();
      const rows: ScheduleRow[] = mySchedules.map((w, i) => {
        const lastRun = logs[i].find((l) => l.status !== 'skipped');
        return { workflow: w, cycle: formatScheduleTime(w), lastRun, nextRun: getNextRun(w, lastRun, now) };
      });
      // 가까운 실행 순 (다음 실행을 모르는 스케줄은 뒤로)
      rows.sort((a, b) => (a.nextRun?.getTime() ?? Infinity) - (b.nextRun?.getTime() ?? Infinity));

      const weekLogs = logs.flat().filter((l) => now.getTime() - new Date(l.startedAt).getTime() <= WEEK_MS);

      setChatbots(bots);
      setSelectedBotId((prev) => (prev && bots.some((b) => b.id === prev) ? prev : bots[0]?.id ?? null));
      setSchedules(rows);
      setMyWorkflows({
        deployed: mine.filter((w) => w.status === 'deployed').length,
        draft: mine.filter((w) => w.status === 'draft').length,
        latest: [...mine].sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        )[0],
      });
      setWeekRuns({
        success: weekLogs.filter((l) => l.status === 'success').length,
        failed: weekLogs.filter((l) => l.status === 'failed').length,
      });
      setLoadedAt(now);
    } catch (err) {
      console.error('Failed to load home data:', err);
      showToast('error', '대시보드 데이터를 불러오지 못했습니다.');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const selectedBot = chatbots.find((b) => b.id === selectedBotId);

  // 고른 챗봇 대화 화면으로 이동하며 질문 전달 (ChatPage 가 받아서 바로 보냄)
  const startChat = (botId: string, message?: string) => {
    const text = message?.trim();
    navigate(`/chat/${botId}`, text ? { state: { initialMessage: text } } : undefined);
  };

  const handleSend = () => {
    if (selectedBot && question.trim()) startChat(selectedBot.id, question);
  };

  const handleQuestionKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="dashboard-loading-spinner" />
        <p>로딩 중...</p>
      </div>
    );
  }

  const nextSchedule = schedules.find((s) => s.nextRun);
  const weekTotal = weekRuns.success + weekRuns.failed;
  const now = loadedAt ?? new Date();
  const myBotCount = chatbots.filter((b) => b.owner === user?.id).length;
  // 다음 실행 시점 묶음 (오늘 / 1~6일 안 / 그 이후 · 미정)
  const nextRunBuckets = schedules.reduce(
    (acc, { nextRun }) => {
      const days = nextRun ? daysFrom(nextRun, now) : Infinity;
      if (days <= 0) acc.today += 1;
      else if (days < 7) acc.week += 1;
      else acc.later += 1;
      return acc;
    },
    { today: 0, week: 0, later: 0 }
  );

  return (
    <>
      {/* 헤더 - 인사말 + 기준 시각 · 새로고침 */}
      <header className="topbar user-home-head">
        <div>
          <p className="user-home-greeting">안녕하세요, <b>{user?.name || '사용자'}</b>님</p>
          <h1 className="topbar-title">
            오늘의 <em className="user-home-highlight">현황</em>이에요
          </h1>
        </div>
        <div className="topbar-actions">
          <div className="dash-updated">
            {loadedAt && <span>{formatUpdatedAt(loadedAt)}</span>}
            <Button color="gray" appearance="outline" size="md" onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon className={refreshing ? 'dash-spin' : undefined} />
              새로고침
            </Button>
          </div>
        </div>
      </header>

      <div className="page-content">
        <div className="dash">
          {/* 통계 카드 - 공통 item-card, 모두 숫자 · 막대 · 범례 · 한 줄 틀
              순서: 관리자 대시보드와 맞춤 (1 워크플로우 · 2 챗봇 · 3~4 화면별) */}
          <section className="dash-stats user-home-stats">
            <StatCard
              icon={<WorkflowIcon />}
              label="내 워크플로우"
              value={myWorkflows.deployed}
              unit="개"
              segments={[
                { label: '배포', value: myWorkflows.deployed, color: 'var(--success)' },
              ]}
              note={myWorkflows.latest ? `최근 수정 ${myWorkflows.latest.name}` : '아직 만든 워크플로우가 없어요'}
              onClick={() => navigate('/workflows')}
            />
            <StatCard
              icon={<ChatIcon />}
              label="사용 가능한 챗봇"
              value={chatbots.length}
              unit="개"
              segments={[
                // 관리자 대시보드 활성 챗봇과 같은 색 · 순서 (관리자 공유 = primary, 사용자 본인 = info)
                { label: '공유받은 챗봇', value: chatbots.length - myBotCount, color: 'var(--primary)' },
                { label: '내 챗봇', value: myBotCount, color: 'var(--info)' },
              ]}
              note={chatbots[0] ? `최근 배포 ${chatbots[0].name}` : '아직 공유된 챗봇이 없어요'}
              onClick={() => scrollTo('user-home-bots')}
            />
            <StatCard
              icon={<ScheduleIcon />}
              label="실행 중인 스케줄"
              value={schedules.length}
              unit="개"
              segments={[
                { label: '오늘', value: nextRunBuckets.today, color: 'var(--primary)' },
                { label: '이번 주', value: nextRunBuckets.week, color: 'var(--info)' },
                { label: '그 이후', value: nextRunBuckets.later, color: 'var(--gray)' },
              ]}
              note={
                nextSchedule?.nextRun
                  ? `다음 실행 ${formatShort(nextSchedule.nextRun)} · ${formatRelativeDay(nextSchedule.nextRun, now)}`
                  : '예정된 실행이 없어요'
              }
              onClick={() => scrollTo('user-home-schedules')}
            />
            <StatCard
              icon={<ClockIcon />}
              label={<>스케줄 실행 결과 <span className="user-home-muted">· 최근 7일</span></>}
              value={weekTotal}
              unit="회"
              segments={[
                { label: '성공', value: weekRuns.success, color: 'var(--success)' },
                { label: '실패', value: weekRuns.failed, color: 'var(--error)' },
              ]}
              note={
                weekTotal > 0
                  ? `성공률 ${Math.round((weekRuns.success / weekTotal) * 100)}%`
                  : '최근 7일 동안 실행 기록이 없어요'
              }
              onClick={() => scrollTo('user-home-schedules')}
            />
          </section>

          <div className="user-home-cols">
            {/* 사용 가능한 챗봇 - 질문 입력 + 목록 */}
            <section className="dash-card" id="user-home-bots">
              <div className="dash-card-head user-home-card-head">
                <h2>사용 가능한 챗봇<Badge color="gray">{chatbots.length}</Badge></h2>
                {chatbots.length > 0 && (
                  <button type="button" className="dash-card-link" onClick={() => navigate('/chat')}>전체 보기 →</button>
                )}
              </div>

              {chatbots.length === 0 ? (
                <div className="dash-empty">
                  <span className="item-card-icon item-card-icon--primary"><BotIcon /></span>
                  <b>사용 가능한 챗봇이 없어요</b>
                  <p>관리자가 챗봇을 배포하고 공유하면 여기에 표시됩니다.</p>
                </div>
              ) : (
                <>
                  <div className="user-home-target-row">
                    <span className="user-home-target">
                      대화할 챗봇<b>{selectedBot?.name}</b>
                    </span>
                    <span className="user-home-key-hint">
                      <kbd>Enter</kbd> 보내기 / <kbd>Shift</kbd>+<kbd>Enter</kbd> 줄바꿈
                    </span>
                  </div>
                  <div className="user-home-composer">
                    <textarea
                      ref={questionRef}
                      rows={2}
                      value={question}
                      onChange={(e) => setQuestion(e.target.value)}
                      onKeyDown={handleQuestionKeyDown}
                      placeholder="무엇을 물어볼까요? 아래 목록에서 챗봇을 고른 뒤 질문을 입력하세요"
                    />
                    <Button
                      color="primary"
                      size="md"
                      className="user-home-send"
                      aria-label="보내기"
                      title="보내기"
                      onClick={handleSend}
                      disabled={!question.trim() || !selectedBot}
                    >
                      <SendIcon />
                    </Button>
                  </div>

                  <div className="user-home-bot-list">
                    <div className="user-home-bot-row user-home-bot-th">
                      <span>챗봇 · {chatbots.length}</span>
                      <span>만든 사람</span>
                      <span>배포일</span>
                      <span />
                    </div>
                    {chatbots.map((bot) => (
                      <div
                        key={bot.id}
                        className={`user-home-bot-row${bot.id === selectedBotId ? ' is-selected' : ''}`}
                        onClick={() => {
                          setSelectedBotId(bot.id);
                          questionRef.current?.focus();
                        }}
                      >
                        <span className="dash-name">
                          <i className="user-home-mono">{bot.name.trim()[0]?.toUpperCase()}</i>
                          <span className="user-home-bot-text">
                            <b>{bot.name}</b>
                            <small>{bot.description || '설명 없음'}</small>
                          </span>
                        </span>
                        <span className="dash-num">{bot.ownerName || '-'}</span>
                        <span className="dash-muted">{formatDate(bot.deployedAt || bot.createdAt)}</span>
                        <Button
                          color="primary"
                          appearance="outline"
                          size="sm"
                          className="user-home-go"
                          onClick={(e) => {
                            e.stopPropagation();
                            startChat(bot.id);
                          }}
                        >
                          <ChatIcon />
                          대화하기
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

            {/* 실행 중인 스케줄 */}
            <section className="dash-card" id="user-home-schedules">
              <div className="dash-card-head user-home-card-head">
                <h2>실행 중인 스케줄<Badge color="gray">{schedules.length}</Badge></h2>
                {schedules.length > 0 && (
                  <button type="button" className="dash-card-link" onClick={() => navigate('/workflows')}>전체 보기 →</button>
                )}
              </div>

              {schedules.length === 0 ? (
                <div className="dash-empty">
                  <span className="item-card-icon item-card-icon--primary"><ScheduleIcon /></span>
                  <b>실행 중인 스케줄이 없어요</b>
                  <p>스케줄 트리거가 있는 워크플로우를 배포하면 여기에 표시됩니다.</p>
                </div>
              ) : (
                <div className="user-home-sch">
                  {nextSchedule?.nextRun && (
                    <div className="user-home-next">
                      <div>
                        <p>가장 가까운 실행</p>
                        <strong>{nextSchedule.workflow.name}</strong>
                      </div>
                      <Badge color="info">{formatLong(nextSchedule.nextRun)}</Badge>
                    </div>
                  )}
                  <div className="user-home-sch-list">
                    {schedules.map(({ workflow, cycle, lastRun, nextRun }) => (
                      <button
                        type="button"
                        key={workflow.id}
                        className="user-home-sch-row"
                        onClick={() => navigate(`/workflows/${workflow.id}`)}
                      >
                        <i className="dash-avatar kb"><ScheduleIcon /></i>
                        <span className="user-home-sch-text">
                          <b>{workflow.name}</b>
                          <span>
                            {cycle || '-'} · 지난 실행{' '}
                            {lastRun ? (
                              <em className={lastRun.status === 'failed' ? 'is-failed' : 'is-success'}>
                                {lastRun.status === 'failed' ? '실패' : '성공'}
                              </em>
                            ) : (
                              '없음'
                            )}
                          </span>
                        </span>
                        <span className="user-home-sch-side">
                          <Badge color="success" dot>실행 중</Badge>
                          {nextRun ? `다음 ${formatShort(nextRun)}` : '다음 실행 -'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserHomePage;
